import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import { api } from '../sync/api';
import type { Saver } from '../sync/saver';
import { mediaKeysIn, sizeText, unusedMedia } from '../video/cleanup';
import { deleteMedia, listMedia, mediaErrorText, spaceLeft } from '../video/media';
import { $ } from './dom';
import { openSheet } from './sheets';
import { toast } from './toast';

/*
 * "พื้นที่วิดีโอในเครื่อง" (choice ก): how much room the 1080p clip copies and music take on this device, and
 * clearing the copies no video work uses. Deleting a video work in "งานของฉัน" also deletes its copies.
 * The original files in the photo library are never touched. See src/video/cleanup.ts for what counts as used.
 */

/** the server list is cut at 200 works (worker/index.ts): with that many, some works were not seen */
const LIST_LIMIT = 200;
const errText = (e: unknown) => e instanceof Error && e.message ? e.message : String(e);

export function initVideoStore(ed: Editor, history: History, saver: Saver, opts: { busy: () => string | null; refresh: () => void }) {
  const msg = $('vgMsg'), clean = $<HTMLButtonElement>('vgClean');
  let armed: ReturnType<typeof setTimeout> | null = null;
  let run = 0;
  const say = (text: string, ok = false) => { msg.textContent = text; msg.hidden = !text; msg.classList.toggle('ok', ok); };
  const cleanLabel = (bytes: number) => `ล้างสำเนาที่ไม่มีงานไหนใช้ (${sizeText(bytes)})`;

  /** keys of the open work: what is on screen plus every undo/redo step */
  function openWorkKeys(): string[] {
    const keys = new Set<string>();
    for (const s of history.steps()) mediaKeysIn(s).forEach(k => keys.add(k));
    if (ed.doc.video) mediaKeysIn(JSON.stringify(ed.doc.video)).forEach(k => keys.add(k));
    return [...keys];
  }

  /**
   * Every copy some work of mine still uses. Throws with a Thai reason when that cannot be known for sure
   * (offline, too many works, a work that cannot be read) — then nothing may be deleted.
   */
  async function usedKeys(skipWork?: string): Promise<Set<string>> {
    if (saver.mode !== 'online') throw new Error(`ต้องออนไลน์ก่อน เพื่อตรวจว่างานวิดีโอไหนยังใช้คลิปอะไร (${saver.reason || 'ยังไม่ได้เชื่อมเซิร์ฟเวอร์'})`);
    const used = new Set<string>();
    const { designs } = await api.list();
    if (designs.length >= LIST_LIMIT) throw new Error(`มีงานบนเซิร์ฟเวอร์ ${LIST_LIMIT} ชิ้นขึ้นไป ระบบตรวจไม่ครบทุกงาน จึงยังไม่ล้าง เพื่อไม่ให้ลบคลิปที่ยังใช้อยู่`);
    for (const d of designs) {
      if (d.kind !== 'video' || d.id === skipWork) continue;
      try { mediaKeysIn((await api.get(d.id)).json).forEach(k => used.add(k)); }
      catch (e) { throw new Error(`อ่านงาน "${d.name}" ไม่ได้ (${errText(e)}) จึงยังไม่ล้าง เพื่อไม่ให้ลบคลิปที่ยังใช้อยู่`); }
    }
    if (!(skipWork && saver.link.id === skipWork)) openWorkKeys().forEach(k => used.add(k));
    return used;
  }

  function disarm() { if (armed) clearTimeout(armed); armed = null; }

  async function paint() {
    const r = ++run;
    disarm();
    say('');
    clean.disabled = true;
    clean.textContent = 'ล้างสำเนาที่ไม่มีงานไหนใช้';
    $('vgUsed').textContent = 'กำลังตรวจว่างานไหนใช้คลิปอะไร…';
    $('vgUnused').textContent = '';
    try {
      const stored = await listMedia();
      if (r !== run) return;
      const total = stored.reduce((n, s) => n + s.bytes, 0);
      $('vgTotal').textContent = `สำเนาคลิปและเพลง ${stored.length} ไฟล์ · ${sizeText(total)}`;
      const free = await spaceLeft();
      let kept = false;
      try { kept = !!(await navigator.storage?.persisted?.()); } catch { kept = false; }
      $('vgFree').textContent = `${free === null ? 'เบราว์เซอร์ไม่บอกพื้นที่ว่าง' : `เบราว์เซอร์ให้ใช้ได้อีกราว ${sizeText(free)}`}`
        + ` · ${kept ? 'เบราว์เซอร์ตกลงเก็บข้อมูลนี้ไว้ถาวรแล้ว' : 'เบราว์เซอร์ยังไม่รับปากว่าจะเก็บไว้ถาวร (กด "เพิ่มไปยังหน้าจอโฮม" แล้วเปิดจากไอคอนช่วยได้)'}`;
      if (!stored.length) { $('vgUsed').textContent = 'ยังไม่มีสำเนาคลิปในเครื่องนี้'; return; }
      const used = await usedKeys();
      if (r !== run) return;
      const unused = unusedMedia(stored, used);
      const unusedBytes = unused.reduce((n, s) => n + s.bytes, 0);
      $('vgUsed').textContent = `ใช้อยู่ในงาน ${stored.length - unused.length} ไฟล์ · ${sizeText(total - unusedBytes)}`;
      $('vgUnused').textContent = unused.length ? `ไม่มีงานไหนใช้ ${unused.length} ไฟล์ · ${sizeText(unusedBytes)}` : 'ไม่มีสำเนาที่ค้างอยู่โดยไม่ได้ใช้';
      clean.disabled = !unused.length || !!opts.busy();
      if (unused.length) clean.textContent = cleanLabel(unusedBytes);
      const busy = opts.busy();
      if (busy && unused.length) say(`${busy} แล้วค่อยล้าง`);
    } catch (e) {
      if (r !== run) return;
      $('vgUsed').textContent = '';
      say(errText(e) + (e instanceof DOMException ? ` (${mediaErrorText(e)})` : ''));
    }
  }

  clean.onclick = async () => {
    const busy = opts.busy();
    if (busy) { say(`${busy} แล้วค่อยล้าง`); return; }
    if (!armed) {
      clean.textContent = 'แตะอีกครั้งเพื่อยืนยันการล้าง';
      armed = setTimeout(() => { void paint(); }, 3500);
      return;
    }
    disarm();
    clean.disabled = true;
    try {
      // check again right now: what was shown may be out of date
      const [stored, used] = await Promise.all([listMedia(), usedKeys()]);
      const unused = unusedMedia(stored, used);
      for (const s of unused) {
        try { await deleteMedia(s.key); }
        catch (e) { throw new Error(`ลบสำเนาบางไฟล์ไม่สำเร็จ: ${mediaErrorText(e)}`); }
      }
      const bytes = unused.reduce((n, s) => n + s.bytes, 0);
      toast(`ล้างสำเนาแล้ว ${unused.length} ไฟล์ ได้พื้นที่คืนราว ${sizeText(bytes)} (ต้นฉบับในคลังภาพไม่ถูกแตะ)`);
      opts.refresh();
      await paint();
      say(`ล้างแล้ว ${unused.length} ไฟล์ · ${sizeText(bytes)}`, true);
    } catch (e) {
      say(errText(e));
      clean.disabled = false;
    }
  };

  $('moreVStore').onclick = () => { openSheet('shVStore'); void paint(); };

  return {
    paint,
    /** media keys of a work about to be deleted (read before the delete; throws with a reason) */
    async keysOf(id: string): Promise<string[]> {
      if (saver.link.id === id && !saver.viewing) return openWorkKeys();
      return mediaKeysIn((await api.get(id)).json);
    },
    /** after a video work was deleted: delete its copies that no other work uses; says why when it cannot */
    async dropCopies(id: string, keys: string[]) {
      if (!keys.length) return;
      const later = ' ล้างภายหลังได้ที่ เพิ่มเติม › พื้นที่วิดีโอในเครื่อง';
      try {
        const busy = opts.busy();
        if (busy) throw new Error(busy);
        const used = await usedKeys(id);
        let n = 0;
        for (const k of keys) {
          if (used.has(k)) continue;
          try { await deleteMedia(k); n++; }
          catch (e) { throw new Error(mediaErrorText(e)); }
        }
        if (n) toast(`ลบสำเนาคลิปของงานนี้ในเครื่องแล้ว ${n} ไฟล์ (ต้นฉบับในคลังภาพไม่ถูกแตะ)`);
        opts.refresh();
      } catch (e) {
        toast(`ลบงานแล้ว แต่ยังล้างสำเนาคลิปในเครื่องไม่ได้: ${errText(e)}${later}`, true);
      }
    }
  };
}

export type VideoStore = ReturnType<typeof initVideoStore>;
